import axios from "axios";
import { Capacitor } from "@capacitor/core";

const IS_NATIVE = (() => { try { return Capacitor.isNativePlatform(); } catch { return false; } })();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Public web origin for share links. Inside the native app
// window.location.origin is "capacitor://localhost" — useless in
// WhatsApp/iMessage — so fall back to the public backend origin.
export const publicOrigin = () =>
  typeof window !== "undefined" && window.location.origin.startsWith("http")
    ? window.location.origin
    : BACKEND_URL;
const TOKEN_KEY = "solano_token";

export const setAuthToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);

export const api = axios.create({
  baseURL: API,
  // Native app authenticates via Bearer token only — sending credentials
  // from capacitor://localhost trips CORS when the server replies with "*".
  withCredentials: !IS_NATIVE,
});

// Shared GET: several widgets poll the same endpoint (friends/unread,
// gamification/summary …). Collapse concurrent calls into one request and
// serve a short-lived cache so a screen mount costs one round-trip per URL.
const _shared = new Map();
export const sharedGet = (url, ttlMs = 4000) => {
  const now = Date.now();
  const hit = _shared.get(url);
  if (hit && (hit.promise || now - hit.at < ttlMs)) return hit.promise || Promise.resolve(hit.res);
  const promise = api.get(url).then((res) => {
    _shared.set(url, { res, at: Date.now() });
    return res;
  }, (err) => {
    _shared.delete(url);
    throw err;
  });
  _shared.set(url, { promise, at: now });
  return promise;
};
export const invalidateShared = (prefix = "") => {
  for (const k of [..._shared.keys()]) if (k.startsWith(prefix)) _shared.delete(k);
};


// Attach Bearer token + user's preferred language on every request
api.interceptors.request.use((config) => {
  if ((config.method || "get").toLowerCase() !== "get") invalidateShared();
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  // Forward user's app language (from I18nContext localStorage) as Accept-Language
  // so backend can localize template titles etc.
  try {
    const lang = localStorage.getItem("solano_lang");
    if (lang) {
      config.headers = config.headers || {};
      config.headers["Accept-Language"] = lang;
    }
  } catch {}
  return config;
});

// Auto-redirect to login when session expires / cookie missing.
// Skip for public endpoints so login/register don't loop.
const PUBLIC_PATHS = ["/auth/login", "/auth/register", "/auth/session", "/auth/me", "/auth/forgot-password", "/auth/reset-password", "/client/notification-prefs", "/client/gamification/loadout"];

// Capture the OAuth-callback state at MODULE LOAD time. AuthCallback wipes the
// hash on mount, so this must be evaluated synchronously when the bundle first
// runs. During the OAuth session_id exchange, transient 401s on protected
// endpoints (e.g. LoadoutContext's initial fetch) must NOT trigger a redirect
// to /login — the AuthCallback flow will establish the session shortly.
const IS_OAUTH_CALLBACK = (() => {
  try { return typeof window !== "undefined" && (window.location.hash || "").includes("session_id="); }
  catch { return false; }
})();

// Public/anonymous frontend routes where an interceptor-triggered redirect to
// /login would either loop or clobber a legitimate visitor. Kept in sync with
// the routes registered in App.js.
const PUBLIC_ROUTES = ["/login", "/register", "/forgot-password", "/reset-password"];

api.interceptors.response.use(
  (r) => {
    // Basic quota headers: the LAST free AI action of the month triggers a
    // friendly Pro invite (UpgradeLimitDialog listens, variant "lastAction").
    try {
      const used = parseInt(r?.headers?.["x-ai-quota-used"], 10);
      const limit = parseInt(r?.headers?.["x-ai-quota-limit"], 10);
      if (limit > 0 && used >= limit) {
        window.dispatchEvent(new CustomEvent("solano:last-free-action"));
      }
    } catch {}
    return r;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    // PRO per-feature monthly limit reached → friendly localized message.
    if (status === 402 && error?.response?.data?.detail === "LIMIT_REACHED") {
      try {
        const l = localStorage.getItem("solano_lang") || "de";
        const d = error.response.data.reset_date;
        const date = d ? new Date(d + "T00:00:00").toLocaleDateString(l === "en" ? "en-GB" : l + "-CH") : "";
        error.response.data.detail = {
          de: `Du hast dein monatliches Limit für diese KI-Funktion erreicht. Dein Kontingent wird am ${date} automatisch erneuert.`,
          en: `You've reached your monthly limit for this AI feature. Your quota renews automatically on ${date}.`,
          it: `Hai raggiunto il limite mensile per questa funzione AI. Il tuo contingente si rinnova automaticamente il ${date}.`,
          fr: `Tu as atteint ta limite mensuelle pour cette fonction IA. Ton quota se renouvelle automatiquement le ${date}.`,
        }[l] || error.response.data.detail;
      } catch {}
      return Promise.reject(error);
    }

    // AI paywall: Basic quota exhausted → friendly upgrade dialog instead of
    // a hard redirect. UpgradeLimitDialog (mounted globally) listens for this.
    if (status === 402 && error?.response?.data?.detail === "UPGRADE_REQUIRED") {
      try {
        window.dispatchEvent(new CustomEvent("solano:upgrade-required", {
          detail: { reason: error?.response?.data?.reason },
        }));
      } catch {}
      // Feature code toasts `detail` directly — replace the raw backend code
      // with a short friendly message in the user's language.
      try {
        const l = localStorage.getItem("solano_lang") || "de";
        error.response.data.detail = {
          de: "Gratis-KI-Limit erreicht",
          en: "Free AI limit reached",
          it: "Limite AI gratuito raggiunto",
          fr: "Limite IA gratuite atteinte",
        }[l] || "Free AI limit reached";
      } catch {}
      return Promise.reject(error);
    }
    // Normalize Pydantic 422 payloads. FastAPI returns `detail` as an
    // ARRAY of error objects (`{type, loc, msg, input, url}`) — callers
    // across the app assume `detail` is a string and pass it straight to
    // `toast.error(...)`, which crashes React with "Objects are not valid
    // as a React child" and blanks the screen (bug reported Feb 2026).
    try {
      const d = error?.response?.data?.detail;
      if (Array.isArray(d)) {
        error.response.data.detail = d
          .map((e) => (typeof e === "string" ? e : (e?.msg || JSON.stringify(e))))
          .join(" · ");
      } else if (d && typeof d === "object") {
        error.response.data.detail = d.msg || JSON.stringify(d);
      }
    } catch { /* ignore — never mask the original error */ }

    if (status === 403 && error.response?.data?.detail === "ACCOUNT_BLOCKED" && !PUBLIC_PATHS.some((p) => url.includes(p))) {
      // Account suspended by an admin → drop the session and show the clear notice on /login.
      setAuthToken(null);
      if (!window.location.pathname.startsWith("/login")) window.location.replace("/login?blocked=1");
      return Promise.reject(error);
    }

    if (status === 401 && !PUBLIC_PATHS.some((p) => url.includes(p))) {
      // Never bounce back to /login while the OAuth callback exchange is
      // still in-flight — the session token is being minted right now.
      if (IS_OAUTH_CALLBACK) return Promise.reject(error);
      // Token invalid on server — clear it and bounce to login
      setAuthToken(null);
      const cur = window.location.pathname + window.location.search;
      const already = PUBLIC_ROUTES.some((p) => window.location.pathname.startsWith(p));
      if (!already) {
        const next = encodeURIComponent(cur);
        window.location.replace(`/login?next=${next}`);
      }
    }
    return Promise.reject(error);
  }
);
